"use client"

import { usePageTransition } from "@/app/(marketing)/_components/page-transition"
import { useCallback, useMemo, useState } from "react"
import { CardGrid } from "./_components/card-grid"
import { CategorySidebar, MobileFilterPills } from "./_components/category-sidebar"
import { ToolSection } from "./_components/tool-section"
import { type UseCaseCategory, useCases } from "./_data/use-cases"
import { t, useLanguage } from "./_lib/i18n"

export default function UseCasesPage() {
  const language = useLanguage()
  const { navigateWithFade } = usePageTransition()
  const [selectedCategory, setSelectedCategory] = useState<UseCaseCategory | null>(null)

  const handleCardClick = useCallback(
    (e: React.MouseEvent, href: string) => {
      e.preventDefault()
      navigateWithFade(href)
    },
    [navigateWithFade]
  )

  const filteredCases = useMemo(
    () => (selectedCategory ? useCases.filter((uc) => uc.category === selectedCategory) : useCases),
    [selectedCategory]
  )

  return (
    <div className="min-h-screen bg-white">
      <style jsx>{`
        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-card-fade-in {
          animation: cardFadeIn 0.5s ease-out;
        }
      `}</style>

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4" style={{ color: "#26251f" }}>
            {t(
              language,
              "Sophyと一緒に、こんなことができる",
              "Here's what you can do with Sophy",
              "和Sophy一起，你可以做到这些"
            )}
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#666" }}>
            {t(
              language,
              "あなたを理解し、あなたの働き方に合わせて進化するパートナー",
              "AI that evolves to match your work style. The more you use it, the more it becomes yours.",
              "AI随着你的工作方式进化。使用越多，越成为你的专属助手。"
            )}
          </p>
        </div>
      </section>

      {/* Sidebar + Cards */}
      <section className="px-4 md:px-6 pb-20">
        <div className="max-w-7xl mx-auto flex items-start gap-8">
          <CategorySidebar language={language} selectedCategory={selectedCategory} onSelect={setSelectedCategory} />
          <div className="flex-1 min-w-0">
            <div className="lg:hidden">
              <MobileFilterPills
                language={language}
                selectedCategory={selectedCategory}
                onSelect={setSelectedCategory}
              />
            </div>
            <CardGrid
              cases={filteredCases}
              selectedCategory={selectedCategory}
              language={language}
              onCardClick={handleCardClick}
            />
          </div>
        </div>
      </section>

      <ToolSection language={language} />
    </div>
  )
}
